import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Plus, Search } from "lucide-react";
import { MakeMonogram } from "@/components/opportunities/StatusBadges";
import { num } from "@/lib/format";
import { searchTree } from "@/lib/grouping";

const Count = ({ value }) => (
  <span className="mono ml-auto shrink-0 pl-2 text-[11px] text-slate-500">{num(value)}</span>
);

export const NavTree = ({
  tree,
  total,
  selection,
  onSelect,
  defaultOpen = true,
  testid = "nav-tree",
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(defaultOpen);
  const [collapsed, setCollapsed] = useState({});

  const visible = useMemo(() => searchTree(tree, query), [tree, query]);
  const isOpen = (key) => !collapsed[key];
  const toggle = (key) => setCollapsed((s) => ({ ...s, [key]: !s[key] }));

  const rowClass = (activeRow) =>
    `flex w-full items-center gap-1.5 rounded-sm px-1.5 py-1 text-left transition-colors duration-150 ${
      activeRow ? "bg-blue-50 text-blue-800" : "text-slate-700 hover:bg-slate-100"
    }`;

  return (
    <section className="panel self-start" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="nav-tree-toggle"
        className="flex w-full items-center justify-between gap-2 border-b border-slate-200 px-3 py-2"
      >
        <span className="label-xs">Merken &amp; modellen</span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-200 ${
            open ? "" : "-rotate-90"
          }`}
        />
      </button>

      {open && (
        <div className="p-2">
          <div className="relative mb-2">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Zoek merk, model of type…"
              data-testid="nav-tree-search"
              className="h-8 w-full rounded-sm border border-slate-300 bg-white pl-7 pr-2 text-[12px] outline-none transition-colors duration-150 focus:border-blue-500"
            />
          </div>

          <button
            type="button"
            onClick={() => onSelect({})}
            data-testid="nav-tree-all"
            className={`${rowClass(!selection.make_key)} mb-1 font-semibold`}
          >
            Alle voertuigen
            <Count value={total} />
          </button>

          <div className="max-h-[62vh] space-y-0.5 overflow-y-auto pr-0.5">
            {visible.length === 0 && (
              <div className="px-1.5 py-3 text-[11.5px] text-slate-500">Geen resultaten.</div>
            )}
            {visible.map((make) => {
              const makeActive = selection.make_key === make.key && !selection.model_key;
              return (
                <div key={make.key}>
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => toggle(make.key)}
                      aria-label={`Model(len) van ${make.label}`}
                      data-testid={`nav-toggle-make-${make.key}`}
                      className="rounded-sm p-0.5 text-slate-400 hover:text-slate-700"
                    >
                      {isOpen(make.key) ? (
                        <ChevronDown className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => onSelect({ make_key: make.key })}
                      data-testid={`nav-make-${make.key}`}
                      className={`${rowClass(makeActive)} font-semibold`}
                    >
                      <MakeMonogram label={make.label} size="sm" />
                      <span className="truncate text-[12.5px]">{make.label}</span>
                      <Count value={make.stats.count} />
                    </button>
                  </div>

                  {isOpen(make.key) &&
                    make.models.map((model) => {
                      const modelKey = `${make.key}/${model.key}`;
                      const modelActive =
                        selection.make_key === make.key &&
                        selection.model_key === model.key &&
                        !selection.type_key;
                      return (
                        <div key={model.key} className="ml-3">
                          <div className="flex items-center">
                            <button
                              type="button"
                              onClick={() => toggle(modelKey)}
                              aria-label={`Uitvoeringen van ${model.label}`}
                              data-testid={`nav-toggle-model-${make.key}-${model.key}`}
                              className="rounded-sm p-0.5 text-slate-400 hover:text-slate-700"
                            >
                              {isOpen(modelKey) ? (
                                <ChevronDown className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5" />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                onSelect({ make_key: make.key, model_key: model.key })
                              }
                              data-testid={`nav-model-${make.key}-${model.key}`}
                              className={rowClass(modelActive)}
                            >
                              <span className="truncate text-[12px] font-medium">
                                {model.label}
                              </span>
                              <Count value={model.stats.count} />
                            </button>
                          </div>

                          {isOpen(modelKey) &&
                            model.types.map((type) => {
                              const typeActive =
                                selection.make_key === make.key &&
                                selection.model_key === model.key &&
                                selection.type_key === type.key;
                              return (
                                <button
                                  key={type.key}
                                  type="button"
                                  onClick={() =>
                                    onSelect({
                                      make_key: make.key,
                                      model_key: model.key,
                                      type_key: type.key,
                                    })
                                  }
                                  data-testid={`nav-type-${make.key}-${model.key}-${type.key}`}
                                  className={`${rowClass(typeActive)} ml-6`}
                                >
                                  <span className="truncate text-[12px]">{type.label}</span>
                                  <Count value={type.stats.count} />
                                </button>
                              );
                            })}
                        </div>
                      );
                    })}
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => navigate("/nieuwe-scan")}
            data-testid="nav-tree-new-search"
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-sm border border-dashed border-slate-300 px-2 py-1.5 text-[12px] font-semibold text-slate-600 transition-colors duration-150 hover:border-blue-400 hover:text-blue-700"
          >
            <Plus className="h-3.5 w-3.5" /> Nieuwe zoekopdracht
          </button>
        </div>
      )}
    </section>
  );
};
